import type { VerifyAuthResponse, WorkspacePlan } from '@common/api/authService';
import type { Workspace, WorkspaceSubscription } from './useWorkspaces';
import {
    readPaymentReturnParams,
    type PaymentReturnParams,
} from '@/utils/paymentReturnParams';

const PLAN_NAME_BY_CODE: Record<string, string> = {
    SOLO_199: 'Solo',
    SOLO_399: 'Solo',
    BASIC: 'Solo',
    SOLO_999: 'Solo',
    SOLO_1299: 'Solo',
    SOLO: 'Solo',
    TEAM_1499: 'Team',
    TEAM_2999: 'Team',
    PRO: 'Team',
    TEAM_9999: 'Team',
    TEAM_49999: 'Team',
    TEAM: 'Team',
};

const PLAN_FAMILY_BY_CODE: Record<string, string> = {
    SOLO_199: 'SOLO',
    SOLO_399: 'SOLO',
    BASIC: 'SOLO',
    SOLO_999: 'SOLO',
    SOLO_1299: 'SOLO',
    SOLO: 'SOLO',
    TEAM_1499: 'TEAM',
    TEAM_2999: 'TEAM',
    PRO: 'TEAM',
    TEAM_9999: 'TEAM',
    TEAM_49999: 'TEAM',
    TEAM: 'TEAM',
};

export interface PaymentReturnPlanOverride {
    workspaceId: string | null;
    planCode: string;
    planName: string | null;
    billingCycle: 'monthly' | 'yearly' | null;
}

export const normalizePaymentReturnPlanCode = (planCode?: string | null): string | null => {
    const normalizedPlanCode = planCode?.trim().toUpperCase();
    return normalizedPlanCode || null;
};

export const normalizePaymentReturnPlanFamily = (planCode?: string | null): string | null => {
    const normalizedPlanCode = normalizePaymentReturnPlanCode(planCode);
    return normalizedPlanCode ? PLAN_FAMILY_BY_CODE[normalizedPlanCode] || normalizedPlanCode : null;
};

export const paymentReturnPlanCodesMatch = (
    expectedPlanCode?: string | null,
    actualPlanCode?: string | null,
): boolean => {
    const expectedPlanFamily = normalizePaymentReturnPlanFamily(expectedPlanCode);
    const actualPlanFamily = normalizePaymentReturnPlanFamily(actualPlanCode);

    if (!expectedPlanFamily) {
        return true;
    }

    return Boolean(actualPlanFamily && expectedPlanFamily === actualPlanFamily);
};

export const resolvePaymentReturnPlanName = (
    planCode?: string | null,
    planName?: string | null,
): string => {
    const normalizedPlanName = planName?.trim();
    if (normalizedPlanName) {
        return normalizedPlanName;
    }

    const normalizedPlanCode = normalizePaymentReturnPlanCode(planCode);
    return normalizedPlanCode ? PLAN_NAME_BY_CODE[normalizedPlanCode] || normalizedPlanCode : 'Bustly';
};

export const workspaceMatchesPaymentReturnPlan = (
    workspace: Workspace | null | undefined,
    planCode?: string | null,
    billingCycle?: string | null,
): boolean => {
    const normalizedPlanCode = normalizePaymentReturnPlanCode(planCode);
    const normalizedBillingCycle = billingCycle === 'monthly' || billingCycle === 'yearly'
        ? billingCycle
        : null;
    if (!normalizedPlanCode && !normalizedBillingCycle) {
        return true;
    }

    const matchesPlanCode = paymentReturnPlanCodesMatch(
        normalizedPlanCode,
        workspace?.subscription?.plan_code,
    );
    const matchesBillingCycle = !normalizedBillingCycle
        || workspace?.subscription?.billing_cycle === normalizedBillingCycle;
    return matchesPlanCode && matchesBillingCycle;
};

export const profileMatchesPaymentReturnPlan = (
    profile: VerifyAuthResponse | null | undefined,
    planCode?: string | null,
    billingCycle?: string | null,
): boolean => {
    const normalizedPlanCode = normalizePaymentReturnPlanCode(planCode);
    const normalizedBillingCycle = billingCycle === 'monthly' || billingCycle === 'yearly'
        ? billingCycle
        : null;
    if (!normalizedPlanCode && !normalizedBillingCycle) {
        return true;
    }

    const matchesPlanCode = paymentReturnPlanCodesMatch(
        normalizedPlanCode,
        profile?.workspacePlan?.planCode,
    );
    const matchesBillingCycle = !normalizedBillingCycle
        || profile?.workspacePlan?.billingCycle === normalizedBillingCycle;
    return matchesPlanCode && matchesBillingCycle;
};

export const resolvePaymentReturnPlanOverride = (
    paymentReturn: PaymentReturnParams,
): PaymentReturnPlanOverride | null => {
    const normalizedPlanCode = normalizePaymentReturnPlanCode(paymentReturn.planCode);
    if (paymentReturn.result !== 'success' || !normalizedPlanCode) {
        return null;
    }

    return {
        workspaceId: paymentReturn.workspaceId,
        planCode: normalizedPlanCode,
        planName: paymentReturn.planName,
        billingCycle: paymentReturn.billingCycle,
    };
};

export const readPaymentReturnPlanOverride = (
    searchParams: URLSearchParams,
    fallbackWorkspaceId?: string | null,
): PaymentReturnPlanOverride | null => {
    return resolvePaymentReturnPlanOverride(readPaymentReturnParams(searchParams, {
        workspaceId: fallbackWorkspaceId,
    }));
};

export const shouldApplyPaymentReturnPlanOverride = ({
    override,
    workspaceId,
}: {
    override: PaymentReturnPlanOverride | null | undefined;
    workspaceId?: string | null;
}): boolean => {
    if (!override) {
        return false;
    }

    const normalizedWorkspaceId = workspaceId?.trim();
    if (!override.workspaceId || !normalizedWorkspaceId) {
        return true;
    }

    return override.workspaceId === normalizedWorkspaceId;
};

export const applyPaymentReturnPlanToProfile = (
    profile: VerifyAuthResponse | null,
    planCode?: string | null,
    planName?: string | null,
    billingCycle?: string | null,
): VerifyAuthResponse | null => {
    const normalizedPlanCode = normalizePaymentReturnPlanCode(planCode);
    if (!profile || !normalizedPlanCode) {
        return profile;
    }

    const previousPlan = profile.workspacePlan;
    const resolvedPlanName = resolvePaymentReturnPlanName(normalizedPlanCode, planName);
    const resolvedBillingCycle = billingCycle === 'monthly' || billingCycle === 'yearly'
        ? billingCycle
        : previousPlan?.billingCycle || 'monthly';
    const nextPlan: WorkspacePlan = {
        planId: previousPlan?.planId || '',
        planCode: normalizedPlanCode,
        planName: resolvedPlanName,
        tier: previousPlan?.tier || normalizedPlanCode.toLowerCase(),
        status: 'active',
        startAt: previousPlan?.startAt || '',
        trialEndAt: '',
        currentPeriodStart: previousPlan?.currentPeriodStart || '',
        currentPeriodEnd: previousPlan?.currentPeriodEnd || '',
        cancelAtPeriodEnd: false,
        currency: previousPlan?.currency || 'usd',
        billingCycle: resolvedBillingCycle,
        provider: previousPlan?.provider || 'stripe',
        title: previousPlan?.title,
        badge: '',
        supportingText: previousPlan?.supportingText,
        actionLabel: 'Manage',
        statusLabel: resolvedPlanName,
        statusMessage: previousPlan?.statusMessage || '',
        primaryAction: 'Manage',
        primaryActionMode: previousPlan?.primaryActionMode,
        nextBillingAt: previousPlan?.nextBillingAt,
        expiresAt: previousPlan?.expiresAt,
        displayBudgetMicrousd: previousPlan?.displayBudgetMicrousd,
        displayUsedMicrousd: previousPlan?.displayUsedMicrousd,
        displayRemainingMicrousd: previousPlan?.displayRemainingMicrousd,
        usageStatus: previousPlan?.usageStatus,
        bannerType: previousPlan?.bannerType,
        bannerTitle: previousPlan?.bannerTitle,
        bannerDescription: previousPlan?.bannerDescription,
        bannerAction: previousPlan?.bannerAction,
        cards: previousPlan?.cards,
    };

    return {
        ...profile,
        workspacePlan: nextPlan,
    };
};

export const applyPaymentReturnPlanToWorkspace = (
    workspace: Workspace,
    planCode?: string | null,
    planName?: string | null,
    billingCycle?: string | null,
): Workspace => {
    const normalizedPlanCode = normalizePaymentReturnPlanCode(planCode);
    if (!normalizedPlanCode) {
        return workspace;
    }

    const previousSubscription = workspace.subscription;
    const resolvedPlanName = resolvePaymentReturnPlanName(normalizedPlanCode, planName);
    const resolvedBillingCycle = billingCycle === 'monthly' || billingCycle === 'yearly'
        ? billingCycle
        : previousSubscription?.billing_cycle || 'monthly';
    const subscription: WorkspaceSubscription = {
        id: previousSubscription?.id || `payment-return-${workspace.id}`,
        plan_id: previousSubscription?.plan_id || '',
        status: 'active',
        current_period_end: previousSubscription?.current_period_end || null,
        trial_end_at: null,
        cancel_at_period_end: false,
        created_at: previousSubscription?.created_at,
        plan_code: normalizedPlanCode,
        plan_name: resolvedPlanName,
        plan_tier: previousSubscription?.plan_tier || normalizedPlanCode.toLowerCase(),
        billing_cycle: resolvedBillingCycle,
        plan_label: resolvedPlanName,
        plan_badge: '',
        status_label: resolvedPlanName,
        status_message: previousSubscription?.status_message || '',
        action_label: 'Manage',
        next_action: 'Manage',
    };

    return {
        ...workspace,
        subscription,
    };
};
